import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Footer } from '../components/FooterBlocks';
import EnseignantShell from './EnseignantShell';
import EleveShell from './EleveShell';
import SecretariatShell from './SecretariatShell';
import { useAuth } from '../context/AuthContext';
import { ArrowRight, CheckCircle2 } from 'lucide-react';

export default function PageShell({ children, minimal }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  // Back-office enseignant isolé : sidebar dédiée sur toutes les pages, sauf l'accueil visiteurs
  if (user?.role === 'enseignant' && pathname !== '/') {
    return <EnseignantShell>{children}</EnseignantShell>;
  }
  // Espace élève isolé (l'admin garde le chrome public — il a son propre back-office /admin)
  if (user?.role === 'eleve' && !user?.is_admin && pathname !== '/') {
    return <EleveShell>{children}</EleveShell>;
  }
  // Back-office secrétariat
  if (user?.role === 'secretaire' && pathname !== '/') {
    return <SecretariatShell>{children}</SecretariatShell>;
  }
  // Mode épuré : toutes les routes /enseignant/* + toutes les pages vues par un enseignant connecté
  const isMinimal = minimal ?? (pathname.startsWith('/enseignant/') || user?.role === 'enseignant');
  return (
    <div className="App min-h-screen bg-[#0a0a0b] text-white">
      <Header hideBanners={isMinimal} />
      {children}
      <Footer minimal={isMinimal} />
    </div>
  );
}

export function PageHero({ data, accent = 'red' }) {
  const navigate = useNavigate();
  const { user, openRegister } = useAuth();
  const onPrimary = () => {
    if (data.primaryAuth && !user) { openRegister(); return; }
    if (data.primaryTo) navigate(data.primaryTo);
  };
  const onSecondary = () => { if (data.secondaryTo) navigate(data.secondaryTo); };
  return (
    <section className="relative mt-6 sm:mt-8 lg:mt-12">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden min-h-[320px] sm:min-h-[440px] lg:min-h-[520px] flex items-end">
          <img src={data.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
          <div className="absolute inset-0 grid-lines opacity-30" />
          <div className="relative z-10 p-5 sm:p-7 lg:p-12 w-full">
            <span className={`inline-block text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold mb-3 sm:mb-4 text-${accent}-400`}>
              {data.badge}
            </span>
            <h1 className="font-serif-editorial text-white text-[28px] sm:text-[44px] lg:text-[80px] leading-[0.98] tracking-tight max-w-5xl text-shadow-strong">
              {data.title[0]} <br /><span className="text-red-500">{data.title[1]}</span>
            </h1>
            <p className="mt-4 sm:mt-6 text-white/70 text-[13.5px] sm:text-[15px] lg:text-[16px] max-w-2xl leading-relaxed">{data.subtitle}</p>
            <div className="mt-5 sm:mt-7 flex flex-wrap gap-2 sm:gap-3">
              <button onClick={onPrimary} data-testid="hero-primary-btn" className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-6 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] sm:text-[14px] font-semibold transition shadow-xl shadow-red-900/40">
                {data.primary} <ArrowRight size={15} />
              </button>
              <button onClick={onSecondary} data-testid="hero-secondary-btn" className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-6 rounded-full border border-white/20 bg-white/5 backdrop-blur text-white text-[13px] sm:text-[14px] font-medium hover:bg-white/10 transition">
                {data.secondary}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function SectionTitle({ kicker, title, sub }) {
  return (
    <div className="mb-6 sm:mb-10">
      {kicker && <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">{kicker}</div>}
      <h2 className="font-serif-editorial text-white text-[24px] sm:text-[34px] lg:text-[48px] leading-[1.02] sm:leading-[0.98] tracking-tight">{title}</h2>
      {sub && <p className="mt-3 sm:mt-4 text-white/60 text-[13.5px] sm:text-[15px] max-w-3xl">{sub}</p>}
    </div>
  );
}

export function FeatureGrid({ items }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {items.map((it, i) => (
        <div key={i} className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-6 hover:border-red-700/40 transition">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 font-mono-spec font-bold text-[12px] sm:text-[13px]">0{i + 1}</div>
          <h4 className="font-serif-editorial text-white text-[17px] sm:text-[20px] mt-3 sm:mt-4 leading-tight">{it.title}</h4>
          <p className="text-white/60 text-[12.5px] sm:text-[13.5px] mt-2 leading-relaxed">{it.desc}</p>
        </div>
      ))}
    </div>
  );
}

export function StatsRow({ items }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
      {items.map((s, i) => (
        <div key={i} className="bg-[#0d0d0e] p-4 sm:p-6 lg:p-8 text-center">
          <div className="font-serif-editorial text-red-500 text-[30px] sm:text-[44px] lg:text-[60px] leading-none">{s.v}</div>
          <div className="text-white/60 text-[11px] sm:text-[12px] mt-2 font-mono-spec uppercase tracking-wider">{s.l}</div>
        </div>
      ))}
    </div>
  );
}

export function ModuleAccordion({ modules }) {
  return (
    <div className="space-y-3">
      {modules.map((m, i) => (
        <div key={i} className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-6 lg:p-7 hover:border-red-700/40 transition">
          <div className="flex items-start gap-3 sm:gap-5">
            <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-red-600/10 border border-red-700/30 flex items-center justify-center text-red-400 font-mono-spec font-bold text-[12px] sm:text-[14px]">{m.code}</div>
            <div className="flex-1">
              <h3 className="font-serif-editorial text-white text-[18px] sm:text-[22px] lg:text-[26px] leading-tight">{m.title}</h3>
              <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                {m.topics.map((t, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-white/70 text-[13.5px]">
                    <CheckCircle2 size={14} className="text-red-400 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
